import { useEffect, Suspense } from 'react'
import { lazyWithRetry } from './utils/lazyImportRetry'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import { LoadingScreen, AdminLoadingScreen } from './components/ui'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { useAvailabilityToasts } from './hooks/useAvailabilityToasts'

// Core pages - loaded immediately (auth flow)
import { Login } from './pages/Login'
import { AuthCallback } from './pages/AuthCallback'

// Buyer portal removed 2026-04-18 (billing module deleted); /buyer-login,
// /dashboard, /calls, /disputes, /transactions, /payments, /buyer/* all gone.

// Lazy-loaded admin pages (heavy components)
// Dashboard V3 "Sovereign Analyst" is the sole admin dashboard (2026-04-18).
// V1, V2, and DashboardRestructured were fully removed; their URLs redirect to V3.
const AdminDashboardV3 = lazyWithRetry(() => import('./pages/admin/DashboardV3'), 'AdminDashboardV3')
// CallLog page removed 2026-04-18 — NumberPerformance + raw-clicks DuckDB
// views replace the per-call table.
// Analytics page removed 2026-04-18 — UnifiedAnalytics was heavily
// buyer-focused; future dashboard slices will use client-side DuckDB.
const AdminChangelog = lazyWithRetry(() => import('./pages/admin/Changelog').then(m => ({ default: m.Changelog })), 'AdminChangelog')
const AdminClicks = lazyWithRetry(() => import('./pages/admin/Clicks').then(m => ({ default: m.Clicks })), 'AdminClicks')
// 2026-04-22: removed Adspect, Templates, DeployTemplate, IconGenerator,
// AdCreativeLibrary, CreativeLibrary, AIBackgroundGenerator,
// FeedCreativesGenerator, NetworkCreatives, Telegram pages along with
// the Creative Tools + Icon + Adspect subsystems.
const AdminNumberPerformance = lazyWithRetry(() => import('./pages/admin/NumberPerformance').then(m => ({ default: m.NumberPerformance })), 'AdminNumberPerformance')
const AdminDialicsAccounts = lazyWithRetry(() => import('./pages/admin/DialicsAccounts').then(m => ({ default: m.DialicsAccounts })), 'AdminDialicsAccounts')
const AdminDidUsage = lazyWithRetry(() => import('./pages/admin/DidUsage').then(m => ({ default: m.DidUsage })), 'AdminDidUsage')
const AdminFBAccounts = lazyWithRetry(() => import('./pages/admin/FacebookAccounts').then(m => ({ default: m.FacebookAccounts })), 'AdminFBAccounts')
const AdminAdAccountsOverview = lazyWithRetry(() => import('./pages/admin/AdAccountsOverview').then(m => ({ default: m.AdAccountsOverview })), 'AdminAdAccountsOverview')
const AdminAdsManagerV2 = lazyWithRetry(() => import('./features/ads-manager-v2').then(m => ({ default: m.AdsManagerWithKernel })), 'AdminAdsManagerV2')
const AdminAdsManagerV3 = lazyWithRetry(() => import('./features/ads-manager-v3/AdsManagerV3'), 'AdminAdsManagerV3')
// AdsManager v1 (pages/admin/AdsManager.jsx, 5985 lines) removed 2026-04-24
// as part of Phase 3 of docs/plans/2026-04-24-stack-consistency-migration.md.
// /admin/ads-manager-v1 now redirects to v2 — see redirect block below.
const AdminHeadlineEngine = lazyWithRetry(() => import('./pages/admin/HeadlineEngine'), 'AdminHeadlineEngine')
const AdminHeadlinePool = lazyWithRetry(() => import('./pages/admin/HeadlinePool'), 'AdminHeadlinePool')
const AdminCurationQueue = lazyWithRetry(() => import('./pages/admin/CurationQueue'), 'AdminCurationQueue')
const AdminSettings = lazyWithRetry(() => import('./pages/admin/Settings').then(m => ({ default: m.Settings })), 'AdminSettings')
const AdminReports = lazyWithRetry(() => import('./pages/admin/Reports'), 'AdminReports')
const AdminAttribution = lazyWithRetry(() => import('./pages/admin/Attribution'), 'AdminAttribution')
const AdminActivityLog = lazyWithRetry(() => import('./pages/admin/ActivityLog'), 'AdminActivityLog')
const AdminFacebookActivityLog = lazyWithRetry(() => import('./pages/admin/FacebookActivityLog'), 'AdminFacebookActivityLog')
const AdminCommentFilter = lazyWithRetry(() => import('./pages/admin/CommentFilter'), 'AdminCommentFilter')
const AdminAccountMap = lazyWithRetry(() => import('./pages/admin/AccountMap'), 'AdminAccountMap')
const AdminProfileFleet = lazyWithRetry(() => import('./pages/admin/ProfileFleet'), 'AdminProfileFleet')
const AdminClickLeak = lazyWithRetry(() => import('./pages/admin/ClickLeak'), 'AdminClickLeak')
const AdminSelfHeal = lazyWithRetry(() => import('./pages/admin/SelfHeal'), 'AdminSelfHeal')
const AdminApprovalLibrary = lazyWithRetry(() => import('./pages/admin/ApprovalLibrary'), 'AdminApprovalLibrary')
const AdminDayCompare = lazyWithRetry(() => import('./pages/admin/DayCompare'), 'AdminDayCompare')
const AdminCampaignBuilder = lazyWithRetry(() => import('./pages/admin/CampaignBuilderBulk'), 'AdminCampaignBuilder')

// Buyer View Routes now reuse buyer pages with preview mode support
// Buyer preview mode removed 2026-04-18 along with the buyer portal.

// Allowed routes per restricted role (role name -> allowed path prefixes)
const ROLE_ALLOWED_ROUTES = {
  junior_media_buyer: ['/admin/dashboard', '/admin/ads-manager', '/admin/account-map', '/admin/number-performance'],
  traffic_manager: ['/admin/dashboard', '/admin/account-map', '/admin/number-performance'],
}

// Protected Route wrapper with permission support
function ProtectedRoute({ children, requireAdmin = false, permission = null, anyPermission = null }) {
  const { isAuthenticated, isLoading, user, permissionsLoaded, isFullAdmin } = useAuthStore()
  const location = useLocation()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // For admin routes, check isAdmin flag (backward compatible)
  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  // Role-based route restriction for non-full-admin users
  if (user?.isAdmin && !isFullAdmin()) {
    const userRoles = user?.roles || []
    const currentPath = location.pathname

    // Check if any of the user's roles have route restrictions
    for (const role of userRoles) {
      const allowed = ROLE_ALLOWED_ROUTES[role.name]
      if (allowed) {
        const isAllowed = allowed.some(prefix => currentPath.startsWith(prefix))
        if (!isAllowed) {
          return <Navigate to="/admin/dashboard" state={{ accessDenied: true }} replace />
        }
      }
    }
  }

  // Permission-based access (RBAC)
  // Full admin users bypass permission checks
  if (!isFullAdmin() && (permission || anyPermission)) {
    const userPermissions = user?.permissions || []

    if (permission && !userPermissions.includes(permission)) {
      return <Navigate to="/admin" state={{ accessDenied: true, required: permission }} replace />
    }

    if (anyPermission && !anyPermission.some(p => userPermissions.includes(p))) {
      return <Navigate to="/admin" state={{ accessDenied: true, required: anyPermission }} replace />
    }
  }

  return children
}

// Redirect based on role
function RoleRedirect() {
  const { isAuthenticated, isLoading, user } = useAuthStore()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.isAdmin) {
    return <Navigate to="/admin" replace />
  }

  return <Navigate to="/dashboard" replace />
}

// Component that monitors destination availability and shows toasts
// Only renders for admin users, returns null (no UI)
function AvailabilityMonitor() {
  useAvailabilityToasts()
  return null
}

export default function App() {
  const { checkAuth, isLoading, isAuthenticated, user } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Prewarm DuckDB-WASM during idle time for authenticated admins so the
  // first Numbers / Dashboard pivot doesn't pay the ~5MB fetch+compile cost
  // on the critical path. Lazy import keeps the module out of the auth
  // bundle; the warmup runs fire-and-forget.
  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) return
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500))
    const cancel = window.cancelIdleCallback || clearTimeout
    const handle = idle(() => {
      import('./services/duckdb').then(m => m.getDuckDB()).catch(() => {})
    }, { timeout: 4000 })
    return () => cancel(handle)
  }, [isAuthenticated, user?.isAdmin])

  // Redirect authenticated users away from login pages
  if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/buyer-login')) {
    return <Navigate to={user?.isAdmin ? '/admin' : '/dashboard'} replace />
  }

  return (
    <WebSocketProvider>
      {/* Monitor destination availability and show toasts for admin users */}
      {isAuthenticated && user?.isAdmin && <AvailabilityMonitor />}

      <Toaster
        position="top-right"
        containerStyle={{
          top: 80,
          right: 16,
        }}
        gutter={8}
        toastOptions={{
          // Default options for standard toast.success/error calls
          duration: 4000,
          style: {
            background: '#18181b',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '12px',
            fontSize: '14px',
            maxWidth: '400px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 6000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
          // Custom toasts (adsToast) handle their own styling
          custom: {
            duration: 5000,
          },
        }}
      />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Root redirect */}
        <Route path="/" element={<RoleRedirect />} />

        {/* Buyer portal + preview routes removed 2026-04-18 (billing module deleted) */}
        <Route path="/buyer-login" element={<Navigate to="/login" replace />} />
        <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Admin routes - with admin-specific loading screen (sidebar placeholder) */}
        {/* Redirect /admin to new dashboard */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Main admin dashboard — uses DashboardV3 "Sovereign Analyst" (cutover Apr 16, 2026) */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminDashboardV3 />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Legacy aliases — old bookmarks/links still land on the active dashboard */}
        <Route path="/admin/dashboard-v3" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard-v2" element={<Navigate to="/admin/dashboard" replace />} />

        {/* Deleted admin pages redirect to main dashboard (buyer/billing/workflow modules all removed) */}
        <Route path="/admin/buyers" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/buyers/:id" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/disputes" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/payments" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/transactions" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/destinations" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/media-buyer" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/analytics" element={<Navigate to="/admin/dashboard" replace />} />

        <Route path="/admin/calls" element={<Navigate to="/admin/dashboard" replace />} />
        <Route
          path="/admin/dialics-accounts"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminDialicsAccounts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/did-usage"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminDidUsage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/changelog"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminChangelog />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/number-performance"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminNumberPerformance />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fb-accounts"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminFBAccounts />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ad-accounts"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminAdAccountsOverview />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ads-manager"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminAdsManagerV2 />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ads-manager-v3"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminAdsManagerV3 />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/campaign-builder"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminCampaignBuilder />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Legacy v1 AdsManager route — kept as a redirect so old bookmarks
            land on v2 instead of 404ing. v1 component + file removed
            2026-04-24 (Phase 3h). */}
        <Route path="/admin/ads-manager-v1" element={<Navigate to="/admin/ads-manager" replace />} />
        {/* /admin/media-buyer removed (buyer module deleted 2026-04-18); redirect handled above */}

        {/* Real Clicks page (Adspect-filtered traffic) */}
        <Route
          path="/admin/real-clicks"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminClicks />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Activity Log - destination availability tracking */}
        <Route
          path="/admin/activity-log"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminActivityLog />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Self-Heal — autonomous account grooming (P0 SHADOW read-only panel) */}
        <Route
          path="/admin/self-heal"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminSelfHeal />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Facebook Activity Log - campaign/adset/ad changes */}
        <Route
          path="/admin/fb-activity"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminFacebookActivityLog />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Comment Filter - auto-delete Facebook comments */}
        <Route
          path="/admin/comment-filter"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminCommentFilter />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Account Map - editable FB account ↔ profile ↔ landing page map */}
        <Route
          path="/admin/account-map"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminAccountMap />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Profile Fleet - AdsPower profiles with BMs, accounts, rejected ads */}
        <Route
          path="/admin/profile-fleet"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminProfileFleet />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Click-Leak Reconciliation - read-only FB → cloaker → Binom funnel */}
        <Route
          path="/admin/click-leak"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminClickLeak />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Approval Library - read-only FB approval/rejection analytics */}
        <Route
          path="/admin/approval-library"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminApprovalLibrary />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approval-library/:tab"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminApprovalLibrary />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Redirects from old URLs (includes all Creative Tools pages and
            Adspect / Icons / Telegram — subsystems deleted 2026-04-22.) */}
        <Route path="/admin/creatives" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/templates" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/icons" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/adspect" element={<Navigate to="/admin/real-clicks" replace />} />
        <Route path="/admin/creative-library" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/ad-creatives" element={<Navigate to="/admin/ads-manager" replace />} />
        <Route path="/admin/ai-backgrounds" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/feed-creatives" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/network-creatives" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/telegram" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/ad-library" element={<Navigate to="/admin/ads-manager" replace />} />
        <Route path="/admin/campaigns" element={<Navigate to="/admin/ads-manager" replace />} />
        <Route path="/admin/insights" element={<Navigate to="/admin/analytics" replace />} />
        <Route path="/admin/clicks" element={<Navigate to="/admin/real-clicks" replace />} />
        <Route path="/admin/numbers" element={<Navigate to="/admin/number-performance" replace />} />

        {/* Headline Engine page */}
        <Route
          path="/admin/headline-engine"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminHeadlineEngine />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/headline-pool"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminHeadlinePool />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/headline-curation"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminCurationQueue />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/headline-library"
          element={<Navigate to="/admin/headline-engine" replace />}
        />

        {/* Settings page - requires admin */}
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminSettings />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/:tab"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminSettings />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Notification settings page removed 2026-04-17. Bell icon in sidebar
            is the only surface. Legacy /admin/notifications + /admin/telegram
            routes removed — if someone lands on them they'll fall through to
            the 404 catch-all. */}

        {/* Reports */}
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminReports />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Day Compare */}
        <Route
          path="/admin/compare"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminDayCompare />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Attribution Engine */}
        <Route
          path="/admin/attribution"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<AdminLoadingScreen />}>
                <AdminAttribution />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </WebSocketProvider>
  )
}
